import React from 'react';
import OutlinedInput, {type OutlinedInputProps as MuiOutlinedInputProps} from '@mui/material/OutlinedInput';
import {type AlignmentProps, extractSystemStyles} from '@famly/mf_system_system-props';
import {useDataProps, useAriaProps} from '@famly/mf_ts-utils_data-aria-props';

export type TextFieldProps = {
    /**
     * If `true`, the `input` element is focused during the first mount.
     */
    autoFocus?: MuiOutlinedInputProps['autoFocus'];

    /**
     * If `true`, the component is disabled.
     * The prop defaults to the value (`false`) inherited from the parent FormControl component.
     */
    disabled?: MuiOutlinedInputProps['disabled'];

    /**
     * The short hint displayed in the `input` before the user enters a value.
     */
    placeholder?: MuiOutlinedInputProps['placeholder'];

    /**
     * The size of the component.
     */
    size?: MuiOutlinedInputProps['size'];

    /**
     * If `true`, the `input` will take up the full width of its container.
     * @default false
     */
    fullWidth?: MuiOutlinedInputProps['fullWidth'];

    /**
     * If `true`, a [TextareaAutosize](/material-ui/react-textarea-autosize/) element is rendered.
     * @default false
     */
    multiline?: MuiOutlinedInputProps['multiline'];

    /**
     * If `true`, the `input` will indicate an error.
     * The prop defaults to the value (`false`) inherited from the parent FormControl component.
     */
    error?: MuiOutlinedInputProps['error'];

    /**
     * Start `InputAdornment` for this component. Will be ignored if `multiline` is `true`.
     */
    startAdornment?: MuiOutlinedInputProps['startAdornment'];

    /**
     * End `InputAdornment` for this component. Will be ignored if `multiline` is `true`.
     */
    endAdornment?: MuiOutlinedInputProps['endAdornment'];

    /**
     * Minimum number of rows to display when multiline option is set to true. Minimum and default value is 3.
     */
    minRows?: Exclude<MuiOutlinedInputProps['minRows'], string>;

    /**
     * Maximum number of rows to display when multiline option is set to true. Minimum value is 3.
     */
    maxRows?: Exclude<MuiOutlinedInputProps['maxRows'], string>;

    /**
     * The change handler called when the component is updated.
     */
    onChange?: MuiOutlinedInputProps['onChange'];

    /**
     * The handler called when the component is focused.
     */
    onFocus?: MuiOutlinedInputProps['onFocus'];

    /**
     * Type of the `input` element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types).
     * @default 'text'
     */
    type?: MuiOutlinedInputProps['type'];

    /**
     * The value that the input component holds
     */
    value?: string;

    /**
     * Name attribute of the input element.
     */
    name?: string;

    /**
     * ID attribute of the input element.
     */
    id?: string;

    /**
     * If true, the input element is required.
     * The prop defaults to the value (false) inherited from the parent FormControl component.
     */
    required?: boolean;

    /**
     * Callback fired when the input is blurred.
     * Notice that the first argument (event) might be undefined.
     */
    onBlur?: MuiOutlinedInputProps['onBlur'];

    /**
     * Callback fired when the field is focused and a key is pressed.
     */
    onKeyDown?: MuiOutlinedInputProps['onKeyDown'];

    /**
     * Callback fired when the field is focused and a key is let go.
     */
    onKeyUp?: MuiOutlinedInputProps['onKeyUp'];

    /**
     * The autoComplete attribute for the input element.
     */
    autoComplete?: MuiOutlinedInputProps['autoComplete'];

    /**
     * HTML Attributes applied to the input element.
     */
    inputProps?: {minLength?: number; maxLength?: number};
} & AlignmentProps;

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            disabled,
            placeholder,
            size,
            multiline,
            fullWidth,
            error,
            startAdornment,
            endAdornment,
            onChange,
            onFocus,
            value,
            type,
            minRows,
            maxRows,
            autoFocus,
            name,
            id,
            required,
            onBlur,
            onKeyDown,
            onKeyUp,
            autoComplete,
            inputProps,
            ...props
        },
        ref,
    ) => {
        const _inputProps = React.useMemo(() => {
            return {...inputProps, 'data-e2e-class': 'text-field-input'};
        }, [inputProps]);

        const dataProps = useDataProps(props);
        const ariaProps = useAriaProps(props);

        const _minRows = Math.max(minRows ?? 0, 3);
        const _maxRows = maxRows ? Math.max(3, maxRows) : undefined;

        const {sx: systemSx} = extractSystemStyles(props);

        return (
            <OutlinedInput
                inputProps={_inputProps}
                placeholder={placeholder}
                size={size}
                disabled={disabled}
                multiline={multiline}
                minRows={_minRows}
                maxRows={_maxRows}
                fullWidth={fullWidth}
                error={error}
                startAdornment={multiline ? undefined : startAdornment}
                endAdornment={multiline ? undefined : endAdornment}
                onChange={onChange}
                onFocus={onFocus}
                value={value}
                inputRef={ref}
                type={type}
                autoFocus={autoFocus}
                name={name}
                id={id}
                required={required}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                autoComplete={autoComplete}
                sx={systemSx}
                {...dataProps}
                {...ariaProps}
            />
        );
    },
);
